import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ErrorHandler, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core'

import { MatSnackBar } from '@angular/material/snack-bar'

import { animate, state, style, transition, trigger } from '@angular/animations'
import { IMAGE_ZOOM_CHANGE_STEP, MAX_IMAGE_ZOOM, MIN_IMAGE_ZOOM } from 'app/_angular/_components/image-viewer.component'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { KirjanpitoImageHandlerService, NaytettavaSivuUusi, TositeJaNaytettavaSivut } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { KirjanpitoTositeService } from 'app/_angular/service/kirjanpito/kirjanpito-tosite.service'
import { KirjaukseenLiitetynTiedostonSivu } from 'app/_jaettu-lemonator/model/kirjanpito'
import { NaytettavaKirjaus } from '../../../kirjanpito/kirjanpito.component'

import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs'
import { TositeTyyppi } from '../../../kirjanpito/tositteet/tosite-standalone.component'

export interface SivuJaAvainTilintarkastus {
  sivunAvain: string
  sivu: KirjaukseenLiitetynTiedostonSivu
}

@Component({
  selector: '[app-kirjanpito-tilintarkastus-tosite-naytto]',
  templateUrl: './tilintarkastus-tosite-naytto.component.html',
  styleUrls: ['./tilintarkastus-tosite-naytto.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    // the fade-in/fade-out animation.
    trigger('fadeInAnimation', [
      // the "in" style determines the "resting" state of the element when it is visible.
      state('in', style({ opacity: 1 })),
      // fade in when created. this could also be written as transition('void => *')
      transition(':enter', [style({ opacity: 0 }), animate(2000)]),
      // fade out when destroyed. this could also be written as transition('void => *')
      transition(':leave', animate(0, style({ opacity: 0 }))),
    ]),
    // the fade-in/fade-out animation.
    trigger('fastFadeInOutAnimation', [
      // the "in" style determines the "resting" state of the element when it is visible.
      state('in', style({ opacity: 1 })),
      // fade in when created. this could also be written as transition('void => *')
      transition(':enter', [style({ opacity: 0 }), animate(150)]),
      // fade out when destroyed. this could also be written as transition('void => *')
      transition(':leave', animate(150, style({ opacity: 0 }))),
    ]),
  ],
  standalone: false,
})
export class KirjanpitoTilintarkastusTositeNayttoComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()

  @Input() scrollContainerRef: ElementRef<HTMLDivElement>
  @Input() naytaRuksi: boolean
  @Input() piilotaYlaControllit: boolean = false
  @Input() naytettavaKirjausObservable: Observable<NaytettavaKirjaus>

  private _isMainEnhancedOn: boolean = false
  @Input() set isMainEnhancedOn(value: boolean) {
    this._updateMainEnhance(this.naytettavatTositteetJaSivutObservable.value, !!value)
    this._isMainEnhancedOn = !!value
  }
  get isMainEnhancedOn(): boolean {
    return this._isMainEnhancedOn
  }

  naytettavatTositteetJaSivutObservable = new BehaviorSubject<TositeJaNaytettavaSivut[]>(null)
  @Input() set naytettavatTositteetJaSivut(sivut: TositeJaNaytettavaSivut[]) {
    if (this.naytettavatTositteetJaSivutObservable.value && sivut) {
      for (const vanhaTosite of this.naytettavatTositteetJaSivutObservable.value) {
        for (const uusiTosite of sivut) {
          if (vanhaTosite.avain === uusiTosite.avain) {
            for (const vanhaSivu of vanhaTosite.sivut) {
              for (const uusiSivu of uusiTosite.sivut) {
                if (vanhaSivu.avain === uusiSivu.avain) {
                  uusiSivu.ladataan = vanhaSivu.ladataan
                  break
                }
              }
            }
            break
          }
        }
      }
    }
    this._updateMainEnhance(sivut, this.isMainEnhancedOn)
    this.naytettavatTositteetJaSivutObservable.next(sivut)
  }
  get naytettavatTositteetJaSivut(): TositeJaNaytettavaSivut[] {
    return this.naytettavatTositteetJaSivutObservable.value
  }

  private _updateMainEnhance(kaikki: TositeJaNaytettavaSivut[], isMainEnhanceOn: boolean) {
    if (!kaikki) {
      return
    }
    for (const tosite of kaikki) {
      for (const sivu of tosite.sivut ?? []) {
        this._enhance(sivu, isMainEnhanceOn)
      }
    }
  }

  @Output() ruksiaNapautettiin: EventEmitter<TositeJaNaytettavaSivut> = new EventEmitter()

  constructor(
    private _errorHandler: ErrorHandler,
    private _changeDetector: ChangeDetectorRef,
    private _asiakasService: AsiakasService,
    private _snackbar: MatSnackBar,
    private _kirjanpitoImageHandlerService: KirjanpitoImageHandlerService,
    private _kirjanpitoTositeService: KirjanpitoTositeService,
    private _lemonaidFirebase: FirebaseLemonaid,
    private _tositeUriService: TositeUriService,
  ) {}

  time() {
    return new Date().getTime()
  }

  async tositeTositeUudessaIkkunassa(tosite: TositeJaNaytettavaSivut) {
    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    if (!asiakas) {
      return
    }
    // asiakkaat/:asiakasAvain/kirjanpidon-tosite/:tositeTyyppi/:tositeAvain
    const tositeTyyppi: TositeTyyppi = tosite.tyyppi
    const url = '/asiakkaat/' + asiakas.avain + '/kirjanpidon-tosite/' + tositeTyyppi + '/' + tosite.avain
    const ikkunanAvain = 'tosite_' + asiakas.avain + '_' + tosite.avain
    this._avaaUudessaIkkunassa(ikkunanAvain, url)
  }

  private _avaaUudessaIkkunassa(avain: string, url: string) {
    const start = window.location.protocol + '//' + window.location.host
    const height = window.screen.height - 100 < 1920 ? window.screen.height - 100 : 1920
    const width = window.screen.width - 50 < 1080 ? window.screen.width - 50 : 1080
    window.open(start + url, avain, 'height=' + height + ',width=' + width + ',modal=yes,alwaysRaised=yes')
  }

  ngOnInit() {}

  async lataaTositeHandler(tosite: TositeJaNaytettavaSivut) {
    try {
      await this._kirjanpitoTositeService.lataaTositeHandler(tosite)
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Tositteen lataaminen epäonnistui.', null, { duration: 2500 })
    }
  }

  sivunLatausValmis(valmis: boolean, sivu: NaytettavaSivuUusi) {
    if (sivu && sivu.ladataan !== !valmis) {
      sivu.ladataan = !valmis
    }
  }

  sivunLatausVirhe(error: any, sivu: NaytettavaSivuUusi) {
    console.error(error)
    if (sivu) {
      sivu.virhe = error ?? 'Tuntematon virhe'
    }
  }

  zoomIn(sivu: NaytettavaSivuUusi) {
    if (sivu?.zoomable) {
      const newMultiplier = Math.min(sivu.zoomMultiplier + IMAGE_ZOOM_CHANGE_STEP, MAX_IMAGE_ZOOM)
      sivu.zoomMultiplier = newMultiplier
    }
  }

  zoomOut(sivu: NaytettavaSivuUusi) {
    if (sivu?.zoomable) {
      const newMultiplier = Math.max(sivu.zoomMultiplier - IMAGE_ZOOM_CHANGE_STEP, MIN_IMAGE_ZOOM)
      sivu.zoomMultiplier = newMultiplier
    }
  }

  rotateRight(sivu: NaytettavaSivuUusi) {
    if (sivu) {
      if (sivu.rotationDegrees === '90') {
        sivu.rotationDegrees = '180'
      } else if (sivu.rotationDegrees === '180') {
        sivu.rotationDegrees = '270'
      } else if (sivu.rotationDegrees === '270') {
        sivu.rotationDegrees = '0'
      } else if (sivu.rotationDegrees === '0') {
        sivu.rotationDegrees = '90'
      }
    }
  }

  private _enhance(sivu: NaytettavaSivuUusi, newValue: boolean) {
    if (sivu?.urlEnhanced) {
      if (sivu?.useEnhancedUrl && !newValue) {
        sivu.ladataan = true
        delete sivu.useEnhancedUrl
      } else if (newValue) {
        sivu.ladataan = true
        sivu.useEnhancedUrl = 1
      }
    }
  }

  enhance(sivu: NaytettavaSivuUusi) {
    if (sivu?.urlEnhanced) {
      if (sivu?.useEnhancedUrl) {
        sivu.ladataan = true
        delete sivu.useEnhancedUrl
      } else {
        sivu.ladataan = true
        sivu.useEnhancedUrl = 1
      }
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ruksiaPainettu(tosite: TositeJaNaytettavaSivut) {
    this.ruksiaNapautettiin.next(tosite)
  }

  trackbyTosite(index: number, tosite: TositeJaNaytettavaSivut) {
    return tosite.avain
  }

  trackBySivu(index: number, sivu: NaytettavaSivuUusi) {
    return sivu.avain
  }
}
